import { useEffect, useRef } from 'react';
import axios from 'axios';

import { useThemeContext } from '../../../../contexts/ThemeContext';
import { resolveColor } from '../../theme/utils/resolveColor';
import { PoweredByQITech } from '../components/PoweredByQITech';
import * as S from './styles';

interface LendingAnalysisProps {
    signData: any;
    setSignData: (data: any) => void;
    setSignerStep: (step: string) => void;
    setAlreadySigned: (v: boolean) => void;
    setCanceled: (v: boolean) => void;
    setExpired: (v: boolean) => void;
    setLendingReproved: (v: boolean) => void;
    signerHash: string;
}

const SIGNED_STATUSES = ['signed', 'sending_sign_receipt', 'receipt_sent', 'analyzing', 'analyzed', 'completed'];
const POLL_INTERVAL_MS = 4000;
const MAX_POLL_DURATION_MS = 1_200_000;

export const PrivatePayrollAuctionLendingAnalysis = ({
    signData,
    setSignData,
    setSignerStep,
    setAlreadySigned,
    setCanceled,
    setExpired,
    setLendingReproved,
    signerHash,
}: LendingAnalysisProps) => {
    const { clientThemeSettings } = useThemeContext();
    const stoppedRef = useRef(false);
    const patchInFlightRef = useRef(false);

    useEffect(() => {
        stoppedRef.current = false;
        patchInFlightRef.current = false;
        const config = { headers: { Authorization: signData.signer_token } };

        const handleEnvelopeStatus = (status: string | undefined): boolean => {
            if (!status) return false;
            if (SIGNED_STATUSES.includes(status)) {
                if (status !== 'signed') setAlreadySigned(true);
                setSignerStep('signed');
                return true;
            }
            if (status === 'canceled') {
                setCanceled(true);
                return true;
            }
            if (status === 'expired') {
                setExpired(true);
                return true;
            }
            return false;
        };

        const advanceViaPatch = async () => {
            if (stoppedRef.current || patchInFlightRef.current) return;
            patchInFlightRef.current = true;
            try {
                const response = await axios.patch(
                    '/signer',
                    { step: 'lending_analysis', event_date: new Date().toISOString() },
                    config,
                );
                if (stoppedRef.current) return;
                stoppedRef.current = true;
                const nextStep = response.data?.next_step;
                setSignData({ ...signData, ...response.data });
                if (!handleEnvelopeStatus(response.data?.status) && nextStep) {
                    setSignerStep(nextStep);
                }
            } catch (error: any) {
                patchInFlightRef.current = false;
                if (error?.response?.status === 401) {
                    stoppedRef.current = true;
                    setSignerStep('login');
                }
            }
        };

        const pollLendingAnalysis = async () => {
            if (stoppedRef.current) return;
            try {
                const response = await axios.get(`/signer/hash/${signerHash}/lending_analysis`);
                if (stoppedRef.current) return;

                if (handleEnvelopeStatus(response.data?.envelope_status)) {
                    stoppedRef.current = true;
                    return;
                }

                const status = response.data?.status;
                if (status === 'reproved') {
                    stoppedRef.current = true;
                    setLendingReproved(true);
                    return;
                }

                if (status === 'approved' || response.data?.soft_unlock_available === true) {
                    advanceViaPatch();
                }
            } catch (error: any) {
                if (error?.response?.status === 401) {
                    stoppedRef.current = true;
                    setSignerStep('login');
                }
            }
        };

        pollLendingAnalysis();
        const interval = setInterval(pollLendingAnalysis, POLL_INTERVAL_MS);
        const timeout = setTimeout(() => {
            stoppedRef.current = true;
            clearInterval(interval);
        }, MAX_POLL_DURATION_MS);

        return () => {
            stoppedRef.current = true;
            clearInterval(interval);
            clearTimeout(timeout);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!clientThemeSettings) return <></>;

    const primary = resolveColor('primary', clientThemeSettings);

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            <S.Content>
                <S.SpinnerWrapper>
                    <S.Spinner $color={primary} />
                </S.SpinnerWrapper>
                <S.Title $color={primary}>Sua proposta está sendo analisada</S.Title>
                <S.Subtitle>
                    Em breve, seu status será atualizado
                </S.Subtitle>
            </S.Content>

            <S.Footer>
                <PoweredByQITech />
            </S.Footer>
        </S.PageWrapper>
    );
};
